import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Communication", "NORMAL", "Admin", "Super Admin", "Bot1", "Bot2", "Other"];

export default function SapCredentials() {
  const [rows, setRows] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ category: "Communication", username: "", password: "" });
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/sap/creds");
      setRows(r.data.rows || []);
      setConfigured(r.data.configured);
    } catch (e) {
      toast.error("Failed to load credentials");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.username.trim()) return toast.error("User Name is required");
    if (!form.password) return toast.error("Password is required to save");
    try {
      await api.post("/sap/creds", form);
      toast.success(`Saved ${form.username}`);
      setForm({ category: "Communication", username: "", password: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const editRow = (row) => setForm({ category: row.category || "Other", username: row.username, password: "" });

  const del = async (username) => {
    if (!window.confirm(`Remove stored credential for ${username}?`)) return;
    try { await api.delete(`/sap/creds/${encodeURIComponent(username)}`); toast.success("Removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try { const r = await api.post("/sap/creds/test"); setTest(r.data); }
    catch { toast.error("Test failed"); }
    setTesting(false);
  };

  const Dot = ({ ok }) => (
    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%",
      background: ok === true ? "#16a34a" : ok === false ? "#dc2626" : "#9ca3af", marginRight: 6 }} />
  );

  return (
    <Layout>
      <div data-testid="sap-credentials-page" style={{ maxWidth: 900, display: "grid", gap: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>SAP Credentials</h1>
          <p style={{ color: "var(--omh-text-muted)", fontSize: 13, marginTop: 6 }}>
            Central store for SAP passwords (encrypted). Updates apply instantly — no redeploy.
            Other Emergent apps read these via the secured broker endpoint.
          </p>
        </div>

        {!configured && (
          <div style={{ background: "#fef2f2", color: "#991b1b", padding: 12, borderRadius: 8, fontSize: 13 }}>
            CRED_ENCRYPTION_KEY is not set — add it as a deployment secret to enable the store.
          </div>
        )}

        {/* Add / update form */}
        <div style={{ border: "1px solid var(--omh-border)", borderRadius: 10, padding: 16, display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Add / Update user</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
            <label style={{ fontSize: 12 }}>Category
              <select data-testid="cred-category" value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                style={{ width: "100%", padding: 8, marginTop: 4 }}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12 }}>User Name
              <input data-testid="cred-username" value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="e.g. _EMERGENTBOM" style={{ width: "100%", padding: 8, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12 }}>Password
              <input data-testid="cred-password" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="current password" style={{ width: "100%", padding: 8, marginTop: 4 }} />
            </label>
            <button data-testid="cred-save-btn" onClick={save}
              style={{ padding: "9px 18px", background: "#0EA5E9", color: "#fff", border: "none",
                borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Save</button>
          </div>
        </div>

        {/* Table */}
        <div style={{ border: "1px solid var(--omh-border)", borderRadius: 10, overflow: "hidden" }}>
          <table data-testid="cred-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--omh-bg-muted, #f8fafc)", textAlign: "left" }}>
                {["Category", "User Name", "Password", "Source", "Updated", ""].map((h) =>
                  <th key={h} style={{ padding: "10px 12px", fontWeight: 700 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ padding: 16 }}>Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={6} style={{ padding: 16 }}>No credentials yet.</td></tr> :
                rows.map((row) => (
                  <tr key={row.username} data-testid={`cred-row-${row.username}`} style={{ borderTop: "1px solid var(--omh-border)" }}>
                    <td style={{ padding: "10px 12px" }}>{row.category || "—"}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 600 }}>{row.username}</td>
                    <td style={{ padding: "10px 12px" }}>{row.password_set ? "••••••••" : "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20,
                        background: row.source === "store" ? "#dcfce7" : "#fef9c3",
                        color: row.source === "store" ? "#166534" : "#854d0e" }}>{row.source}</span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--omh-text-muted)", fontSize: 12 }}>
                      {row.updated_at ? row.updated_at.slice(0, 16).replace("T", " ") : "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button data-testid={`cred-edit-${row.username}`} onClick={() => editRow(row)}
                        style={{ marginRight: 8, background: "none", border: "none", color: "#0EA5E9", cursor: "pointer", fontWeight: 600 }}>Update</button>
                      {row.source === "store" && (
                        <button data-testid={`cred-del-${row.username}`} onClick={() => del(row.username)}
                          style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 600 }}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Test connection */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button data-testid="cred-test-btn" onClick={runTest} disabled={testing}
            style={{ padding: "9px 18px", background: "#111827", color: "#fff", border: "none",
              borderRadius: 8, fontWeight: 700, cursor: "pointer", opacity: testing ? 0.6 : 1 }}>
            {testing ? "Testing…" : "Test SAP connection"}</button>
          {test && (
            <div data-testid="cred-test-result" style={{ fontSize: 13 }}>
              <span style={{ marginRight: 18 }}><Dot ok={test.soap?.ok} />SOAP ({test.soap?.user}) {test.soap?.ok ? "OK" : (test.soap?.reason || "FAILED")}</span>
              <span><Dot ok={test.odata?.ok} />OData ({test.odata?.user}) {test.odata?.ok ? "OK" : (test.odata?.reason || "FAILED")}</span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
